import type { JSX } from 'react';
import type { FileRejection } from 'react-dropzone';
import { useEffect, useMemo, useState } from 'react';
import { ErrorCode } from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';
import type { FilesUrlBase } from '@common/enums';
import type { ProcessActivityAsset, S3Asset } from '@common/validation/processActivityAsset';
import { AssetCategory } from '@common/enums';
import { imageAndPdfExtensions } from '@common/utils/fileExtensions';
import { formatString } from '@common/utils/formatString';
import { attachmentLimit } from '@common/validation/asset';
import { ViewerModal } from '@frontend/components/elements/viewerModal/ViewerModal';
import { getUploadUrl, uploadAssetWithProgress } from '@frontend/services/api/assetsApiService';
import { createThumbnail, createUpload, deleteUpload } from '@frontend/services/api/uploadsApiService';
import { getSelectedLanguage } from '@frontend/translate';
import { getFileType, mapExtensionsToMimeTypes } from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { Dropzone } from './Dropzone';

type DropzoneAsset = ProcessActivityAsset & {
    thumbnailUrl?: string;
    uploadId?: string;
};

type UploadingDropzoneAsset = DropzoneAsset & {
    abort: () => void;
    isUploading: true;
    uploadProgressPercentage: number;
};

type DropzoneFile = DropzoneAsset & {
    key: string;
    mimeType: string;
};

type AssetsDropzoneProps = Readonly<{
    acceptExtensions?: string;
    assets: DropzoneAsset[];
    category?: AssetCategory;
    isAutoSaveEnabled?: boolean;
    maxAssets?: number;
    maxSize?: number;
    onChange: (assets: DropzoneAsset[]) => void;
    onUploadComplete?: () => void;
    onUploadStart?: () => void;
    validationError?: string;
    viewerId?: string;
    viewerUrlBase: FilesUrlBase;
}>;

/*
 * The assets in the dropzone can have four different kinds of states:
 * - Existing assets: Assets that have already been uploaded and are part of the parent state [DatabaseAsset]
 * - Uploading assets: Assets that are being uploaded to storage (not part of the parent state) [UploadingDropzoneAsset]
 * - Pending assets: Assets that have been uploaded to storage and will be added to the parent state [S3Asset]
 * - Uploaded asset: Assets that have been uploaded and added to the parent state, but not yet in assets table [S3Asset]
 *   The uploaded assets will be converted to existing assets when the parent state is saved (for auto save instantly).
 */
export function AssetsDropzone(props: AssetsDropzoneProps): JSX.Element {
    const {
        acceptExtensions,
        assets,
        category,
        isAutoSaveEnabled,
        maxAssets,
        maxSize,
        onChange,
        onUploadComplete,
        onUploadStart,
        validationError,
        viewerId,
        viewerUrlBase,
    } = props;
    const abortSignal = useAbortSignal();
    const [dropzoneErrors, setDropzoneErrors] = useState<string[]>([]);
    const [pendingAddToParent, setPendingAddToParent] = useState<S3Asset[]>([]);
    const [uploadedThumbs, setUploadedThumbs] = useState<{ s3Id: string; url: string }[]>([]);
    const [uploadingAssets, setUploadingAssets] = useState<UploadingDropzoneAsset[]>([]);
    const [viewerOpenedWithAsset, setViewerOpenedWithAsset] = useState<DropzoneFile>();

    const extensions = acceptExtensions ?? imageAndPdfExtensions;
    const assetsOfRelevantCategory = assets.filter(
        (asset) => (!category || asset.category === category) && asset.category !== AssetCategory.removed
    );
    const files = [...assetsOfRelevantCategory, ...pendingAddToParent, ...uploadingAssets].map(
        (asset: DropzoneAsset | UploadingDropzoneAsset) => ({
            ...asset,
            isUploading: 'isUploading' in asset ? asset.isUploading : false,
            key: asset.s3Id ?? asset.id,
            mimeType: getFileType({ name: asset.filename, type: '' }),
            thumbnailUrl: getThumbnailUrl(asset),
            uploadProgressPercentage: 'uploadProgressPercentage' in asset ? asset.uploadProgressPercentage : undefined,
        })
    );
    const uploadGroupId = useMemo(() => uuidv4(), [viewerId]);

    const maxNumberOfFiles = maxAssets ?? attachmentLimit;
    const maxNumberOfFilesLeft = maxNumberOfFiles - files.length;

    // "pendingAddToParent" stores assets that have not been added to the parent-state.
    // These cannot be added in the "uploadFile" function, because that would use a stale version of the "assets" prop,
    // especially when adding multiple files at once.
    useEffect(() => {
        if (pendingAddToParent.length > 0) {
            onChange([...assets, ...pendingAddToParent]);
            setPendingAddToParent([]);
        }
    }, [pendingAddToParent]);

    useEffect(() => {
        if (pendingAddToParent.length === 0 && uploadingAssets.length === 0) {
            onUploadComplete?.();
        }
    }, [pendingAddToParent, uploadingAssets]);

    return (
        <>
            <Dropzone
                acceptedMimeTypes={mapExtensionsToMimeTypes(extensions)}
                errorTexts={validationError ? [validationError, ...dropzoneErrors] : dropzoneErrors}
                files={files}
                maxFiles={maxNumberOfFilesLeft}
                maxSize={maxSize}
                onFileRemove={handleFileRemove}
                onFilesAdded={handleFilesAdded}
                onFilesRejected={handleFilesRejected}
                onThumbnailClick={handleThumbnailClick}
            />
            {viewerOpenedWithAsset && (
                <ViewerModal
                    assets={files}
                    group={uploadGroupId}
                    id={viewerId}
                    isPdf={viewerOpenedWithAsset.filename.toLowerCase().endsWith('.pdf')}
                    onClose={() => setViewerOpenedWithAsset(undefined)}
                    selectedFileId={viewerOpenedWithAsset.id ?? viewerOpenedWithAsset.uploadId}
                    url=""
                    urlBase={viewerUrlBase}
                />
            )}
        </>
    );

    function getThumbnailUrl(asset: DropzoneAsset): string | undefined {
        const uploadedThumb = uploadedThumbs.find((thumb) => thumb.s3Id === asset.s3Id);
        if (uploadedThumb) {
            return uploadedThumb.url;
        }

        if (asset.thumbnailUrl) {
            return asset.thumbnailUrl;
        }

        if (asset.id && viewerId) {
            return `/api/${viewerUrlBase}/${viewerId}/assets/${asset.id}?thumb=true`;
        }
    }

    function getErrorText(error: string): string {
        switch (error) {
            case ErrorCode.FileInvalidType:
                return formatString(getSelectedLanguage().dropzone.canOnlyUploadTheseFiles, [extensions]);
            case ErrorCode.FileTooLarge:
                return getSelectedLanguage().dropzone.fileTooBig;
            case ErrorCode.FileTooSmall:
                return getSelectedLanguage().dropzone.fileTooSmall;
            case ErrorCode.TooManyFiles:
                return formatString(getSelectedLanguage().dropzone.maxFilesExceeded, [`${maxNumberOfFiles}`]);
            default:
                return getSelectedLanguage().dropzone.uploadError;
        }
    }

    async function createAssetThumbnail(asset: DropzoneAsset & S3Asset): Promise<void> {
        const { url } = await createThumbnail(
            {
                filename: asset.filename,
                getThumbnailDataUrl: true,
                id: asset.s3Id,
            },
            abortSignal
        );

        if (url) {
            setUploadedThumbs((prev) => [...prev, { s3Id: asset.s3Id, url }]);
            if (asset.thumbnailUrl) {
                URL.revokeObjectURL(asset.thumbnailUrl);
            }
        }
    }

    async function uploadFile(file: File): Promise<void> {
        const { id: s3Id, url } = await getUploadUrl(file.type, abortSignal);
        const { abort, uploadCompletedPromise } = uploadAssetWithProgress(url, file, (progress) =>
            updateUploadProgress(s3Id, progress)
        );

        const asset: UploadingDropzoneAsset = {
            abort,
            category,
            filename: file.name,
            isUploading: true,
            s3Id,
            thumbnailUrl: URL.createObjectURL(file),
            uploadProgressPercentage: 0,
        };

        setUploadingAssets((prev) => [...prev, asset]);
        await uploadCompletedPromise;

        let wasUploadCancelled = false;
        setUploadingAssets((prev) => {
            wasUploadCancelled = !prev.some((a) => a.s3Id === s3Id);

            return prev.filter((a) => a.s3Id !== s3Id);
        });

        if (wasUploadCancelled) {
            return;
        }

        const { id: uploadId } = await createUpload(
            {
                filename: file.name,
                group: uploadGroupId,
                storageName: s3Id,
            },
            abortSignal
        );

        setPendingAddToParent((prev) => [
            ...prev,
            { ...asset, isUploading: false, uploadId, uploadProgressPercentage: undefined },
        ]);
        await createAssetThumbnail(asset);
    }

    function updateUploadProgress(s3Id: string, uploadProgressPercentage: number): void {
        setUploadingAssets((prev) => {
            const index = prev.findIndex((a) => a.s3Id === s3Id);
            if (index === -1) {
                return prev;
            }
            const newAsset = { ...prev[index], uploadProgressPercentage };
            const newValue = [...prev];
            newValue.splice(index, 1, newAsset);

            return newValue;
        });
    }

    async function handleFilesAdded(addedFiles: File[]): Promise<void> {
        onUploadStart?.();
        if (addedFiles.length > maxNumberOfFilesLeft) {
            setDropzoneErrors([getErrorText(ErrorCode.TooManyFiles)]);

            return;
        }
        setDropzoneErrors([]);
        await Promise.all(addedFiles.map(uploadFile));
    }

    async function handleFilesRejected(rejectedFiles: FileRejection[]): Promise<void> {
        const errors = rejectedFiles.map((file) => file.errors.map((error) => getErrorText(error.code))).flat();
        setDropzoneErrors([...new Set(errors)]);
    }

    async function handleFileRemove(asset: DropzoneAsset | UploadingDropzoneAsset): Promise<void> {
        if (!asset.uploadId && !asset.id) {
            // The upload is in progress, remove the asset from the uploading list and abort the upload
            if ('abort' in asset) {
                asset.abort();
            }

            return setUploadingAssets((prev) => prev.filter((a) => a.s3Id !== asset.s3Id));
        }

        let nextAssets;
        if (asset.id && isAutoSaveEnabled) {
            // Set remove flag for either previously saved or newly uploaded file. Will be processed on save
            nextAssets = assets.map((a) => (a.id === asset.id ? { ...a, category: AssetCategory.removed } : a));
        } else if (asset.id) {
            // Remove previously saved file (for non auto-save), will be processed on save
            nextAssets = assets.filter((a) => a.id !== asset.id);
        } else {
            // Remove file that has not been saved by the parent yet
            nextAssets = assets.filter((a) => a.s3Id !== asset.s3Id);
            if (asset.uploadId) {
                // do not await the delete call, we want to call onChange as soon as possible.
                deleteUpload(asset.uploadId, abortSignal);
            }
        }

        onChange(nextAssets);
    }

    function handleThumbnailClick(file: DropzoneFile): void {
        setViewerOpenedWithAsset(file);
    }
}
